import React, { useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { BrainCircuit, TrendingUp, TrendingDown, Activity, HandCoins, ArrowRight, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { useFirstLoginBriefing, type MonthlyDataPoint } from '../../hooks/useFirstLoginBriefing';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const FirstLoginBriefingPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<{ userData: any; companyData: any }>();
  const companyId = userData?.companyId;
  const navigate = useNavigate();

  const { briefing, loading, dismiss } = useFirstLoginBriefing(companyId);

  const formatMonth = (m: string) => {
    const [year, month] = m.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${year}`;
  };

  const chartData = useMemo(() =>
    (briefing?.monthlyTrend ?? [])
      .slice()
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(m => ({
        month: formatMonth(m.month),
        trail: m.trailCents / 100,
        upfront: m.upfrontCents / 100,
      })),
    [briefing?.monthlyTrend],
  );

  const handleDismiss = async () => {
    await dismiss();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader icon={BrainCircuit} title="Your Commission Briefing" badge="AI" />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-gray-400">Loading briefing...</div>
        </div>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="space-y-6">
        <PageHeader icon={BrainCircuit} title="Your Commission Briefing" badge="AI" />
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-gray-400">No briefing available yet.</div>
        </div>
      </div>
    );
  }

  const {
    executiveSummary,
    trailDropOffs,
    droppedCount,
    newCount,
    netChange,
    keyInsights,
    bookHealthScore,
    bookHealthBucket,
    bookHealthNarrative,
    portfolioStats,
    monthlyTrend,
    comparisonWindow,
  } = briefing;

  const healthColor =
    bookHealthBucket === 'critical' ? '#ef4444' :
    bookHealthBucket === 'needs_attention' ? '#f59e0b' :
    bookHealthBucket === 'healthy' ? '#22c55e' : '#3b82f6';

  const healthLabel =
    bookHealthBucket === 'critical' ? 'Critical' :
    bookHealthBucket === 'needs_attention' ? 'Needs Attention' :
    bookHealthBucket === 'healthy' ? 'Healthy' : 'Strong';

  const formatDollars = (cents: number) =>
    (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BrainCircuit}
        title="Your Commission Briefing"
        badge="AI"
        subtitle={`AI-generated analysis comparing ${formatMonth(comparisonWindow.startMonth)} to ${formatMonth(comparisonWindow.endMonth)} (complete months only)`}
      />

      {/* Health Score + Executive Summary */}
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4">
        {/* Health Gauge */}
        <div className="bg-gray-900/80 border border-white/[0.06] rounded-2xl p-6 flex flex-col items-center justify-center">
          <div
            className="w-[120px] h-[120px] rounded-full flex flex-col items-center justify-center border-[6px]"
            style={{ borderColor: healthColor }}
          >
            <span className="text-4xl font-bold" style={{ color: healthColor }}>{bookHealthScore}</span>
            <span className="text-[11px] text-gray-400 uppercase tracking-wider">Health</span>
          </div>
          <span className="text-sm font-semibold mt-2" style={{ color: healthColor }}>{healthLabel}</span>
        </div>

        {/* Executive Summary */}
        <div className="bg-gray-900/80 border border-white/[0.06] rounded-2xl p-6">
          <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3">Executive Summary</h3>
          <p className="text-[15px] text-gray-300 leading-relaxed">{executiveSummary}</p>
          {bookHealthNarrative && (
            <p className="text-sm text-gray-400 mt-3 italic">{bookHealthNarrative}</p>
          )}
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Loans with Trail"
          value={portfolioStats.lineOfTrail.toLocaleString()}
          sub="All-time loans with commissions"
          icon={<Activity className="w-4 h-4 text-blue-400" />}
          accentColor="blue"
        />
        <StatCard
          label={`Monthly Trail (${formatMonth(comparisonWindow.endMonth)})`}
          value={formatDollars(portfolioStats.monthlyTrailCents)}
          sub="Trail commission revenue"
          icon={<TrendingUp className="w-4 h-4 text-green-400" />}
          accentColor="green"
        />
        <StatCard
          label="Total Commission (All Time)"
          value={formatDollars(portfolioStats.totalCommissionCents)}
          sub={`${formatDollars(portfolioStats.totalUpfrontCents ?? 0)} upfront + ${formatDollars(portfolioStats.totalTrailCents ?? 0)} trail`}
          icon={<HandCoins className="w-4 h-4 text-purple-400" />}
          accentColor="purple"
        />
        <StatCard
          label="Loan Trail Drop-Offs"
          value={`${droppedCount} loans`}
          sub={`${formatMonth(comparisonWindow.startMonth)} to ${formatMonth(comparisonWindow.endMonth)}`}
          icon={netChange >= 0 ? <TrendingUp className="w-4 h-4 text-green-400" /> : <TrendingDown className="w-4 h-4 text-amber-400" />}
          accentColor={netChange >= 0 ? 'green' : 'amber'}
          trend={netChange >= 0 ? `Net +${netChange} (${newCount} gained)` : `Net ${netChange} (${newCount} gained)`}
          trendUp={netChange >= 0}
        />
      </div>

      {/* Commission Trend Chart */}
      <div className="bg-gray-900/80 border border-white/[0.06] rounded-2xl p-5">
        <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-4">Monthly Commission Trend (12 Months)</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="gradTrail" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradUpfront" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}K`} />
            <Tooltip
              contentStyle={{ background: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }}
              formatter={(value: number, name: string) => [`$${value.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`, name === 'trail' ? 'Trail' : 'Upfront']}
            />
            <Area type="monotone" dataKey="trail" stroke="#3b82f6" fill="url(#gradTrail)" strokeWidth={2} />
            <Area type="monotone" dataKey="upfront" stroke="#22c55e" fill="url(#gradUpfront)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
        <div className="flex justify-center gap-6 mt-2">
          <div className="flex items-center gap-2 text-xs text-gray-400"><div className="w-2 h-2 rounded-sm bg-green-500" /> Upfront</div>
          <div className="flex items-center gap-2 text-xs text-gray-400"><div className="w-2 h-2 rounded-sm bg-blue-500" /> Trail</div>
        </div>
      </div>

      {/* Trail Drop-Offs Table */}
      {trailDropOffs.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <h3 className="text-base font-semibold text-white">
              Loan Trail Drop-Offs: {formatMonth(comparisonWindow.startMonth)} to {formatMonth(comparisonWindow.endMonth)}
            </h3>
            <span className="bg-red-500/15 text-red-400 text-[11px] font-semibold px-2 py-0.5 rounded-full">
              {droppedCount} dropped, {newCount} new
            </span>
          </div>
          <div className="bg-gray-900/80 border border-white/[0.06] rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-900/95">
                  <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 border-b border-white/[0.06]">Broker</th>
                  <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 border-b border-white/[0.06]">Lender</th>
                  <th className="text-left text-[11px] uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 border-b border-white/[0.06]">Loan ID</th>
                  <th className="text-right text-[11px] uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 border-b border-white/[0.06]">{formatMonth(comparisonWindow.startMonth)} Trail</th>
                  <th className="text-right text-[11px] uppercase tracking-wider text-gray-500 font-semibold px-4 py-3 border-b border-white/[0.06]">Annual Risk</th>
                </tr>
              </thead>
              <tbody>
                {trailDropOffs.map((d, i) => (
                  <tr key={d.loanId || i} className="hover:bg-blue-500/5 border-b border-white/[0.03] last:border-b-0">
                    <td className="px-4 py-3 text-[13px] text-gray-300 font-medium">{d.brokerName}</td>
                    <td className="px-4 py-3 text-[13px] text-gray-400">{d.lender}</td>
                    <td className="px-4 py-3 text-[12px] text-gray-500 font-mono">{d.loanId}</td>
                    <td className="px-4 py-3 text-[13px] text-red-400 font-semibold text-right tabular-nums">-{formatDollars(d.prevMonthCents)}</td>
                    <td className="px-4 py-3 text-[13px] text-amber-400 font-medium text-right tabular-nums">{formatDollars(d.annualRiskCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-center py-3 text-xs text-gray-500">
              Showing top {trailDropOffs.length} of {droppedCount} dropped loans. Net position:{' '}
              <span className={netChange >= 0 ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold'}>
                {netChange >= 0 ? '+' : ''}{netChange} loans
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Key Insights */}
      {keyInsights.length > 0 && (
        <div>
          <h3 className="text-base font-semibold text-white mb-3">Key Insights</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {keyInsights.map((insight, i) => (
              <div key={i} className="bg-gray-900/80 border border-white/[0.06] rounded-xl p-4 flex gap-3">
                <div className="w-7 h-7 rounded-full bg-blue-500/15 text-blue-400 flex items-center justify-center text-[13px] font-bold shrink-0">
                  {i + 1}
                </div>
                <p className="text-[13px] text-gray-300 leading-relaxed">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="text-center py-8">
        <button
          onClick={handleDismiss}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-12 py-3.5 rounded-xl text-base font-semibold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all"
        >
          Go to Dashboard
          <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-gray-500 mt-2">This briefing will be available in your settings if you want to revisit it</p>
      </div>
    </div>
  );
};

function StatCard({
  label,
  value,
  sub,
  icon,
  accentColor,
  trend,
  trendUp,
}: {
  label: string;
  value: string;
  sub: string;
  icon: React.ReactNode;
  accentColor: 'blue' | 'green' | 'purple' | 'amber';
  trend?: string;
  trendUp?: boolean;
}) {
  const borderColors = {
    blue: 'from-blue-600 to-blue-500',
    green: 'from-green-600 to-green-500',
    purple: 'from-purple-600 to-purple-500',
    amber: 'from-amber-600 to-amber-500',
  };

  return (
    <div className="bg-gray-900/80 border border-white/[0.06] rounded-2xl p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r ${borderColors[accentColor]}`} />
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-[11px] text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{sub}</div>
      {trend && (
        <div className={`text-xs font-medium mt-1 ${trendUp ? 'text-green-400' : 'text-red-400'}`}>
          {trendUp ? '\u25B2' : '\u25BC'} {trend}
        </div>
      )}
    </div>
  );
}

export default FirstLoginBriefingPage;
